import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

const Profile = () => {
  const { user } = useAuth();
  const { address } = useParams();
  const navigate = useNavigate();
  const [publicProfile, setPublicProfile] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(false);

  const isOwnProfile = !address || (user?.address && address.toLowerCase() === user.address.toLowerCase());

  // Fetch public profile
  useEffect(() => {
    const fetchPublicProfile = async () => {
      if (address && (!user?.address || address.toLowerCase() !== user.address.toLowerCase())) {
        try {
          const response = await fetch(`https://app.bitsport.gg/api/users/${address}`);
          if (!response.ok) throw new Error('Failed to fetch profile');
          const data = await response.json();
          setPublicProfile(data.profile);
          setIsFollowing(data.profile.followers?.includes(user?.address));
          setIsBlocked(data.profile.blockedBy?.includes(user?.address));
        } catch (error) {
          console.error('Error fetching profile:', error);
          toast.error('Failed to load profile');
        }
      }
      // If viewing own address URL, redirect to /profile
      else if (address && user?.address && address.toLowerCase() === user.address.toLowerCase()) {
        navigate('/profile');
      }
    };

    fetchPublicProfile();
  }, [address, user, navigate]);

  const handleFollow = async () => {
    if (!user?.isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://app.bitsport.gg/api/users/${address}/follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to update follow status');
      
      setIsFollowing(!isFollowing);
      toast.success(isFollowing ? 'Unfollowed user' : 'Following user');
    } catch (error) {
      console.error('Error following user:', error);
      toast.error('Failed to update follow status');
    } finally {
      setLoading(false);
    }
  };

  const handleBlock = async () => {
    if (!user?.isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`https://app.bitsport.gg/api/users/${address}/block`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to update block status');
      
      setIsBlocked(!isBlocked);
      toast.success(isBlocked ? 'Unblocked user' : 'Blocked user');
    } catch (error) {
      console.error('Error blocking user:', error);
      toast.error('Failed to update block status');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.isConnected && isOwnProfile) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Connect Your Wallet</h2>
          <p className="text-gray-400 mb-6">Please connect your wallet to view your profile</p>
          {/* @ts-expect-error Temporary fix for React 19 */}
          <appkit-button />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {isOwnProfile ? (
        <div className="bg-gray-800 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-primary mb-6">Profile Settings</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-gray-300 mb-2">Wallet Address</label>
              <div className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300">
                {user.address}
              </div>
            </div>
            <div>
              <label className="block text-gray-300 mb-2">Connection Status</label>
              <div className="w-full p-2 rounded bg-dark border border-gray-700 text-gray-300">
                {user.isConnected ? 'Connected' : 'Disconnected'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-start gap-6">
            <img
              src={publicProfile?.profilePicture || '/default-avatar.png'}
              alt={address}
              className="w-32 h-32 rounded-full object-cover border-4 border-primary"
            />
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-gray-200">
                    {address?.slice(0, 6)}...{address?.slice(-4)}
                  </h3>
                  {publicProfile?.bio && (
                    <p className="text-gray-400 mt-2">{publicProfile.bio}</p>
                  )}
                </div>
                {user?.isConnected && user.address !== address && (
                  <div className="space-x-3">
                    <button
                      onClick={handleFollow}
                      disabled={loading}
                      className={`px-4 py-2 rounded ${
                        isFollowing
                          ? 'bg-gray-600 hover:bg-gray-700'
                          : 'bg-primary hover:bg-primary-dark'
                      } text-white transition-colors`}
                    >
                      {isFollowing ? 'Unfollow' : 'Follow'}
                    </button>
                    <button
                      onClick={handleBlock}
                      disabled={loading}
                      className={`px-4 py-2 rounded ${
                        isBlocked
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-gray-600 hover:bg-gray-700'
                      } text-white transition-colors`}
                    >
                      {isBlocked ? 'Unblock' : 'Block'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
